import { FeatureFlag, MultiselectOption, Package } from '@cbo/shared-library';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { object } from 'yup';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import ActionsDialog from '../../components/ActionsDialog/ActionsDialog';
import CentralDashboard from '../../components/CentralDashboard/CentralDashboard';
import routes from '../../constants/routes';
import { usePackage } from '../../contexts/packageContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useUsers } from '../../contexts/userContext';
import useYupValidationResolver from '../../utils/formUtils/yupValidationResolver';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';
import { mapSchemaValues } from '../../utils/reportingUtils/filterUtils';
import { CustomReportsViewerRole, VALID_CUSTOM_REPORTS_VIEWER_ROLES } from '../requests/index';
import {
  useCustomReportDeleteMutation,
  useUnpublishReportMutation,
  useUserPreferencesMutation,
} from '../requests/mutations';
import {
  useGetCustomReportsQuery,
  useGetUserPreferencesQuery,
  useSharedCustomReportsQuery,
  useScheduledReportsQuery,
} from '../requests/queries';
import CreateReportDialog from './components/CreateReportDialog';
import CreateReportDropdown from './components/CreateReportDropDown';
import PublishReportDialog, { PublishMode } from './components/PublishReportDialog';
import { reportAccessMap } from './components/ReportAccessMap';
import ReportSection from './components/ReportSection/ReportSection';
import ScheduleReportDialog from './components/ScheduleReportDialog';
import { scheduleIconButtonSx } from './styles';
import { checkHasAllCustomReportsViewAccess } from './utils/PublishReportUtils';
import invalidateCustomReportsQuery from './utils/QueryUtils';
import { scheduleReportButtonSx } from '../../scheduleReports/styles';
import { ReactComponent as ManageReports } from '../../../assets/ManageReports.svg';
import { filterReportsByTab } from '../../scheduleReports/utils/ViewScheduleReportUtil';
import { ScheduledReportsTabId } from '../../scheduleReports/types';

const SCHEDULE_MODE_CREATE = 'create';

function CentralReports() {
  const { t } = useTranslation();
  const { t: centralReportsT } = useTranslation('translation', { keyPrefix: 'admin.centralReportsPage' });
  const { t: unpublishT } = useTranslation('translation', { keyPrefix: 'admin.centralReportsPage.unpublishReport' });
  const user = useUsers();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { data: userPreferences, isLoading } = useGetUserPreferencesQuery(user.org?.bslId);
  const deleteCustomReportMutation = useCustomReportDeleteMutation();
  const hasCoreAnalytics = usePackage(Package.ADVANCED_ANALYTICS);
  const userPreferencesMutation = useUserPreferencesMutation();
  const isCustomReportsEnabled = useFeatureFlag(FeatureFlag.customReports);

  const hasAllCustomReportsViewAccess = checkHasAllCustomReportsViewAccess(user);

  const { data: orgCustomReports = [], isLoading: orgCustomReportsLoading } = useGetCustomReportsQuery(
    isCustomReportsEnabled && hasCoreAnalytics && hasAllCustomReportsViewAccess
  );

  const shouldFetchSharedReports =
    isCustomReportsEnabled &&
    hasCoreAnalytics &&
    !hasAllCustomReportsViewAccess &&
    !!user.profile?.userId &&
    !!user.bslAuth?.roles;

  // Filter user roles to only include valid custom reports viewer roles
  const validUserRoles =
    user.bslAuth?.roles?.filter((role): role is CustomReportsViewerRole =>
      VALID_CUSTOM_REPORTS_VIEWER_ROLES.includes(role as CustomReportsViewerRole)
    ) || [];

  const { data: sharedCustomReports = [], isLoading: sharedCustomReportsLoading } = useSharedCustomReportsQuery(
    user.profile?.userId ?? '',
    validUserRoles,
    shouldFetchSharedReports
  );

  const customReports = hasAllCustomReportsViewAccess ? orgCustomReports : sharedCustomReports;
  const customLoading =
    isCustomReportsEnabled && (hasAllCustomReportsViewAccess ? orgCustomReportsLoading : sharedCustomReportsLoading);

  const unpublishReportMutation = useUnpublishReportMutation();
  const { setSnackbarState } = useSnackbar();

  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showManagePublishModal, setShowManagePublishModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<number | undefined>(undefined);
  const [selectedReportName, setSelectedReportName] = useState<string | undefined>(undefined);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const favorites = useMemo(() => userPreferences?.favoriteReports ?? [], [userPreferences?.favoriteReports]);
  const isScheduleReportEnabled = useFeatureFlag(FeatureFlag.scheduledReport);
  const isPublishReportEnabled = useFeatureFlag(FeatureFlag.publishCustomReports);
  const hideAlohaEssentialOrgs = useFeatureFlag(FeatureFlag.hideAlohaEssentialOrgs);
  const pageFocusRef = useRef<HTMLDivElement>(null);

  // Fetch scheduled reports to conditionally show manage button
  const { data: scheduledReportsData, isLoading: isScheduledReportsLoading } = useScheduledReportsQuery(
    isScheduleReportEnabled && user.fullyAuthenticated === 'authenticated'
  );
  const hasScheduledReports = (scheduledReportsData?.total ?? 0) > 0;

  // Filter scheduled reports where current user is a recipient using existing utility
  const scheduledToYouReports = filterReportsByTab(
    scheduledReportsData?.schedules ?? [],
    user,
    ScheduledReportsTabId.SentToYou
  );
  const scheduledToYouCount = scheduledToYouReports.length;
  const shouldShowScheduledForYouSection = isScheduleReportEnabled && scheduledToYouCount > 0;

  // Utility function to refetch custom reports based on user access level
  const handleInvalidateCustomReportsQuery = () => {
    invalidateCustomReportsQuery(queryClient, hasAllCustomReportsViewAccess);
  };

  // Temporary. Fetch from API endpoint once we implement letting users create new categories.
  const categoryOptions = [
    ...(isCustomReportsEnabled
      ? [{ id: 'customReports', label: centralReportsT('sectionHeaders.customReports') }]
      : []),
    ...(!hideAlohaEssentialOrgs ? [{ id: 'laborReports', label: centralReportsT('sectionHeaders.laborReports') }] : []),
    { id: 'inventoryReports', label: centralReportsT('sectionHeaders.inventoryReports') },
    { id: 'salesReports', label: centralReportsT('sectionHeaders.salesReports') },
  ];

  const statusOptions = [
    { id: 'published', label: centralReportsT('dropdown.published') },
    { id: 'unpublished', label: centralReportsT('dropdown.unpublished') },
  ];

  const applicationOptions: MultiselectOption[] = [
    { label: centralReportsT('dropdown.asm'), id: centralReportsT('dropdown.asm') },
  ];

  const formContext = useForm({
    mode: 'onChange',
    defaultValues: {
      applications: applicationOptions,
      categories: [],
      statuses: [],
    },
    resolver: useYupValidationResolver(object(mapSchemaValues(['applications']))),
  });

  const applications = formContext.watch('applications') || [];
  const selectedIds = applications.map((app: { id: string }) => app.id);
  const asmId = centralReportsT('dropdown.asm');
  const shouldShowReports = selectedIds.length === 0 || selectedIds.includes(asmId);

  const categories = formContext.watch('categories') || [];

  const statuses = formContext.watch('statuses') || [];
  const selectedStatusIds = statuses.map((s: { id: string }) => s.id);

  const filterByStatus = (report: { isPublished?: boolean; isScheduled?: boolean }) => {
    if (selectedStatusIds.length === 0) return true;
    return selectedStatusIds.includes(report.isPublished ? 'published' : 'unpublished');
  };

  // Derive favorites filtered by selected categories/status for the Favorites section
  const selectedCategoryIds: string[] = (categories || []).map((c: { id: string }) => c.id);
  const filteredFavoriteIds: number[] = useMemo(() => {
    if (!favorites?.length) return [];
    if (!selectedCategoryIds.length && !selectedStatusIds.length) return favorites;

    const customIds = new Set<number>(customReports.map((r) => r.id));

    // Filter out all non-custom reports from candidate set when status filtering is applied since only custom
    // reports have publication status
    const candidates = (
      selectedStatusIds.length > 0 ? favorites.filter((id: number) => customIds.has(id)) : favorites
    ) as number[];

    const allowedIds = new Set<number>();

    // Include custom report favorites if 'customReports' category is selected or none are
    if (selectedCategoryIds.includes('customReports') || selectedCategoryIds.length === 0) {
      candidates.forEach((id: number) => {
        if (customIds.has(id)) allowedIds.add(id);
      });
    }

    // Only include built-in favorites when no status filtering is applied
    if (selectedStatusIds.length === 0) {
      const entries = Object.values(reportAccessMap);
      const byId = new Map<number, string>(entries.map((e) => [e.id, e.section]));
      candidates.forEach((id) => {
        const sectionKey = byId.get(id); // e.g., 'admin.centralReportsPage.sectionHeaders.salesReports'
        if (!sectionKey) return;
        const suffix = sectionKey.split('.').pop(); // 'salesReports'
        if (suffix && selectedCategoryIds.includes(suffix)) allowedIds.add(id);
      });
    }

    return Array.from(allowedIds);
  }, [favorites, selectedCategoryIds, customReports, selectedStatusIds]);

  const onPublishReportClick = (reportId: number, reportName: string) => {
    setSelectedReportId(reportId);
    setSelectedReportName(reportName);
    setShowPublishModal(true);
  };

  const onManagePublishReportClick = (reportId: number, reportName: string) => {
    setSelectedReportId(reportId);
    setSelectedReportName(reportName);
    setShowManagePublishModal(true);
  };

  const onUnPublishReportClick = (reportId: number, reportName: string) => {
    setSelectedReportId(reportId);
    setSelectedReportName(reportName);
    setShowUnpublishModal(true);
  };

  const onDeleteReportClick = async (reportId: number, reportName: string) => {
    setSelectedReportId(reportId);
    setSelectedReportName(reportName);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedReportId(undefined);
    setSelectedReportName(undefined);
  };

  const handleUnpublishClose = () => {
    setShowUnpublishModal(false);
    setSelectedReportId(undefined);
    setSelectedReportName(undefined);
  };

  const handlePublishDialogsClose = () => {
    setShowPublishModal(false);
    setShowManagePublishModal(false);
    setSelectedReportId(undefined);
    setSelectedReportName(undefined);
  };

  const handleUnpublishAction = async () => {
    if (!selectedReportId) return;

    try {
      await unpublishReportMutation.mutateAsync({ reportId: selectedReportId });

      // Close dialog after successful unpublish
      handleUnpublishClose();

      // Refetch custom reports to update the UI
      invalidateCustomReportsQuery(queryClient, hasAllCustomReportsViewAccess);

      // Show success notification
      setSnackbarState({
        open: true,
        message: unpublishT(`success`),
        color: 'success',
      });
    } catch (error) {
      // Show error notification
      setSnackbarState({
        open: true,
        message: unpublishT(`error`),
        color: 'error',
      });
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReportId) return;
    try {
      await deleteCustomReportMutation.mutateAsync({ id: selectedReportId });
      invalidateCustomReportsQuery(queryClient, hasAllCustomReportsViewAccess);
      handleDeleteClose();
      setSnackbarState({
        open: true,
        message: t('admin.customReports.deleteSuccessSnackbar', { name: selectedReportName }),
        color: 'success',
      });
    } catch (error) {
      setSnackbarState({
        open: true,
        message: t('admin.customReports.deleteErrorSnackbar', { name: selectedReportName }),
        color: 'error',
      });
    }
  };

  const onScheduleReportsClick = (reportId: number | string, mode = '') => {
    if (!isScheduleReportEnabled) return;
    setShowScheduleModal(false);
    if (mode === SCHEDULE_MODE_CREATE) {
      history.push(routes.ADD_SCHEDULE_REPORTS.replace(':reportId', String(reportId)));
      return;
    }
    history.push(routes.EDIT_SCHEDULE_REPORTS.replace(':reportId', String(reportId)));
  };

  // Focus back to page container after closing modals
  const resetPageFocus = () => {
    setTimeout(() => pageFocusRef.current?.focus({ preventScroll: true }), 0);
  };

  const handleFavoritesClick = async (updatedFavorites: number[]) => {
    await userPreferencesMutation.mutateAsync({ preferences: { favoriteReports: updatedFavorites } });
  };

  const createReportSection = (cat: string) => (
    <ReportSection
      key={cat}
      sectionHeader={cat}
      search={search}
      favorites={favorites}
      customReports={customReports.filter(filterByStatus)}
      onPublishReportClick={onPublishReportClick}
      onManagePublishReportClick={onManagePublishReportClick}
      onUnPublishReportClick={onUnPublishReportClick}
      onScheduleReportClick={(reportId: number | string) => {
        onScheduleReportsClick(reportId, SCHEDULE_MODE_CREATE);
      }}
      onDeleteReportClick={onDeleteReportClick}
      onFavoritesClick={handleFavoritesClick}
    />
  );

  const canShowPublishDialogs =
    isPublishReportEnabled && selectedReportId !== undefined && selectedReportName !== undefined;

  const renderCategories = () => {
    let categoriesToRender = categories.length === 0 ? categoryOptions : categories;
    if (selectedStatusIds.length > 0) {
      categoriesToRender = categoriesToRender.filter(
        (cat: { id: string; label: string }) => cat.id === 'customReports'
      );
    }
    return categoriesToRender.map((cat: { id: string; label: string }) => createReportSection(cat.label));
  };

  const shouldShowManageScheduledReportsButton =
    isScheduleReportEnabled && hasScheduledReports && !isScheduledReportsLoading;

  const headerActions = (
    <>
      {shouldShowManageScheduledReportsButton && (
        <Tooltip title={t('admin.customReports.manageScheduledReports')}>
          <IconButton
            onClick={() => history.push(routes.SCHEDULED_REPORTS_LIST)}
            data-testid='manage-scheduled-reports-button'
            aria-label={t('admin.customReports.manageScheduledReports')}
            sx={scheduleIconButtonSx}
          >
            <SvgIcon component={ManageReports} />
          </IconButton>
        </Tooltip>
      )}
      {isCustomReportsEnabled ? (
        <CreateReportDropdown
          onCreateReport={() => setShowCreateModal(true)}
          onScheduleReport={() => setShowScheduleModal(true)}
          sx={{
            backgroundColor: isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main,
            color: theme.palette.getContrastText(isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main),
          }}
        />
      ) : (
        isScheduleReportEnabled && (
          <Button
            variant='contained'
            onClick={() => setShowScheduleModal(true)}
            data-testid='schedule-report-button'
            sx={scheduleReportButtonSx}
          >
            {t('admin.customReports.scheduleReport')}
          </Button>
        )
      )}
    </>
  );

  return (
    <>
      <CreateReportDialog
        open={showCreateModal}
        handleClose={() => {
          setShowCreateModal(false);
          resetPageFocus();
        }}
      />
      {canShowPublishDialogs && (
        <PublishReportDialog
          open={showPublishModal}
          handleClose={handlePublishDialogsClose}
          reportId={selectedReportId}
          reportName={selectedReportName}
          onPublishSuccess={handleInvalidateCustomReportsQuery}
          mode={PublishMode.PUBLISH}
        />
      )}
      {canShowPublishDialogs && (
        <PublishReportDialog
          open={showManagePublishModal}
          handleClose={handlePublishDialogsClose}
          reportId={selectedReportId}
          reportName={selectedReportName}
          onPublishSuccess={handleInvalidateCustomReportsQuery}
          mode={PublishMode.MANAGE}
        />
      )}
      {canShowPublishDialogs && (
        <ActionsDialog
          open={showUnpublishModal}
          dataTestId='unpublish-dialog'
          dialogTitle={unpublishT(`title`, { reportName: selectedReportName })}
          onClose={handleUnpublishClose}
          dialogContentText={unpublishT(`content`)}
          closeButtonText={t('buttonText.cancel')}
          closeButtonVariant='text'
          actionButtonText={unpublishT(`confirm`)}
          actionButtonColor='error'
          onActionClick={handleUnpublishAction}
          disableActionButton={unpublishReportMutation.isLoading}
        />
      )}
      {isScheduleReportEnabled && (
        <ScheduleReportDialog
          open={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            resetPageFocus();
          }}
          onNext={(reportId: number | string) => {
            onScheduleReportsClick(reportId, SCHEDULE_MODE_CREATE);
          }}
        />
      )}
      <ActionsDialog
        open={showDeleteModal}
        dataTestId='delete-confirm-dialog'
        onActionClick={handleDeleteReport}
        onClose={handleDeleteClose}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        dialogTitle={`${t('admin.customReports.deleteReport')}?`}
        dialogContentText={t('admin.customReports.deleteReportConfirmation')}
        dialogContentTextColor='text.secondary'
        dialogContentTextVariant='body1'
        actionButtonText={t('admin.customReports.delete')}
        actionButtonColor='error'
        disableActionButton={deleteCustomReportMutation.isLoading}
      />

      <FormContainer formContext={formContext}>
        <CentralDashboard
          loading={isLoading || customLoading}
          loadingTestId='spinner-container'
          containerRef={pageFocusRef}
          headerTestId='centralized-reports-header'
          title={t('navItems.reports')}
          description={centralReportsT(`pageSubheader`)}
          headerActions={headerActions}
          filters={{
            categories: {
              controllerName: 'categories',
              options: categoryOptions,
              singular: centralReportsT('dropdown.category'),
              plural: centralReportsT('dropdown.plural.category'),
              titleText: centralReportsT('dropdown.plural.category'),
            },
            statuses: isCustomReportsEnabled
              ? {
                  controllerName: 'statuses',
                  options: statusOptions,
                  singular: centralReportsT('dropdown.status'),
                  plural: centralReportsT('dropdown.plural.status'),
                  titleText: centralReportsT('dropdown.plural.status'),
                }
              : undefined,
          }}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: t('inventory.counting.searchPlaceholder'),
            testId: 'central-reports-search',
          }}
        >
          {shouldShowReports && (
            <>
              <ReportSection
                sectionHeader={centralReportsT('sectionHeaders.favorites')}
                search={search}
                favorites={filteredFavoriteIds}
                allFavorites={favorites}
                customReports={customReports.filter(filterByStatus)}
                onScheduleReportClick={(reportId: number | string) => {
                  onScheduleReportsClick(reportId, SCHEDULE_MODE_CREATE);
                }}
                onPublishReportClick={onPublishReportClick}
                onManagePublishReportClick={onManagePublishReportClick}
                onUnPublishReportClick={onUnPublishReportClick}
                onFavoritesClick={handleFavoritesClick}
                onDeleteReportClick={onDeleteReportClick}
              />
              {shouldShowScheduledForYouSection && (
                <ReportSection
                  sectionHeader={centralReportsT('sectionHeaders.scheduledForYou')}
                  search={search}
                  favorites={[]}
                  customReports={[]}
                  scheduledReports={scheduledToYouReports}
                />
              )}
              {renderCategories()}
            </>
          )}
        </CentralDashboard>
      </FormContainer>
    </>
  );
}

export default CentralReports;
